import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Car, Users, FileText, User, LogOut, Loader2, Upload, TrendingUp, CheckCircle2, Bookmark, X,
} from "lucide-react";
import { getUser, authHeaders, clearAuth, API } from "@/lib/auth";

const DEAL_STATUS = {
  registada: { label: "Registada", cls: "bg-blue-50 text-blue-700" },
  reservada: { label: "Reservada", cls: "bg-amber-50 text-amber-700" },
  vendida: { label: "Vendida", cls: "bg-emerald-50 text-emerald-700" },
  disponivel: { label: "Disponível", cls: "bg-slate-100 text-slate-500" },
};

const fmt = (n) => new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

export default function SellerDashboard() {
  const navigate = useNavigate();
  const user = getUser();
  const [tab, setTab] = useState("viaturas");
  const [me, setMe] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  // deal form
  const [showDealForm, setShowDealForm] = useState(false);
  const [dealForm, setDealForm] = useState({ client_name: "", client_phone: "", client_email: "", vehicle_id: "", margin: "", notes: "" });
  const [dealSaving, setDealSaving] = useState(false);
  const [docDeal, setDocDeal] = useState(null);
  const [docFiles, setDocFiles] = useState([]);
  const [docUploading, setDocUploading] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "vendedor") { navigate("/entrar"); return; }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    try {
      const [m, v, d] = await Promise.all([
        axios.get(`${API}/auth/me`, { headers: authHeaders() }),
        axios.get(`${API}/seller/vehicles`, { headers: authHeaders() }),
        axios.get(`${API}/seller/deals`, { headers: authHeaders() }),
      ]);
      setMe(m.data);
      setVehicles(v.data);
      setDeals(d.data);
    } catch (err) {
      if (err?.response?.status === 401) { clearAuth(); navigate("/entrar"); return; }
      if (err?.response?.status === 403) { toast.error(err?.response?.data?.detail || "Sem permissão."); }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => { clearAuth(); navigate("/"); };

  const openDealForm = (vehicle) => {
    setDealForm({ client_name: "", client_phone: "", client_email: "", vehicle_id: vehicle.id, margin: "", notes: "" });
    setShowDealForm(true);
    setTab("negociacoes");
  };

  const submitDeal = async (e) => {
    e.preventDefault();
    if (dealSaving) return;
    setDealSaving(true);
    try {
      await axios.post(`${API}/seller/deals`, { ...dealForm, margin: parseInt(dealForm.margin || "0", 10) }, { headers: authHeaders() });
      toast.success("Cliente registado e viatura associada.");
      setShowDealForm(false);
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao registar o cliente.");
    } finally {
      setDealSaving(false);
    }
  };

  const setDealStatus = async (deal, status) => {
    try {
      await axios.patch(`${API}/seller/deals/${deal.id}/status`, { status }, { headers: authHeaders() });
      toast.success(`Negociação marcada como ${DEAL_STATUS[status].label.toLowerCase()}.`);
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao atualizar o estado.");
    }
  };

  const uploadDocs = async (e) => {
    e.preventDefault();
    if (docUploading || docFiles.length === 0) return;
    setDocUploading(true);
    try {
      const data = new FormData();
      docFiles.forEach((f) => data.append("files", f));
      await axios.post(`${API}/seller/deals/${docDeal.id}/documents`, data, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      toast.success("Documentos enviados.");
      setDocDeal(null);
      setDocFiles([]);
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar documentos.");
    } finally {
      setDocUploading(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) { toast.error("As palavras-passe não coincidem."); return; }
    setPwSaving(true);
    try {
      await axios.put(`${API}/client/password`, { current_password: pw.current_password, new_password: pw.new_password }, { headers: authHeaders() });
      toast.success("Palavra-passe alterada.");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao alterar a palavra-passe.");
    } finally {
      setPwSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="seller-loading">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const myVehicleIds = new Set(deals.filter((d) => ["registada", "reservada"].includes(d.status)).map((d) => d.vehicle_id));
  const totalMargin = deals.filter((d) => d.status === "vendida").reduce((s, d) => s + (d.margin || 0), 0);
  const paidMargin = deals.filter((d) => d.status === "vendida" && d.commission_paid).reduce((s, d) => s + (d.margin || 0), 0);
  const pendingMargin = totalMargin - paidMargin;
  const clients = deals;
  const reservations = deals.filter((d) => d.status === "reservada");
  const sales = deals.filter((d) => d.status === "vendida");

  const TABS = [
    { id: "viaturas", label: "Minhas Viaturas", icon: Car },
    { id: "negociacoes", label: "Clientes e Negociações", icon: Users },
    { id: "documentos", label: "Documentos", icon: FileText },
    { id: "perfil", label: "Perfil", icon: User },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="seller-dashboard">
      <header className="bg-[#0B1E36] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-display font-bold tracking-tight">SOLUTION CAR PREMIUM</span>
            <span className="text-[10px] uppercase tracking-widest text-slate-400 border border-white/20 px-2 py-0.5">Vendedor</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-xs text-slate-300 hover:text-white" data-testid="seller-view-site">Ver website</Link>
            <button onClick={logout} data-testid="seller-logout" className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white">
              <LogOut className="w-3.5 h-3.5" /> Sair
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-display text-2xl text-[#0072C6]">Olá, {me?.name || "Vendedor"}</h1>
          <p className="text-sm text-slate-500 mt-1">O seu painel de vendedor — gira clientes, negociações e comissões.</p>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-5 mb-8">
          <div className="bg-white border border-slate-200 p-6" data-testid="stat-clients">
            <p className="text-xs uppercase tracking-wider text-slate-500">Clientes</p>
            <p className="mt-2 font-display text-3xl text-[#0072C6]">{clients.length}</p>
          </div>
          <div className="bg-white border border-slate-200 p-6" data-testid="stat-reservations">
            <p className="text-xs uppercase tracking-wider text-slate-500">Reservas</p>
            <p className="mt-2 font-display text-3xl text-[#0072C6]">{reservations.length}</p>
          </div>
          <div className="bg-white border border-slate-200 p-6" data-testid="stat-margin">
            <p className="text-xs uppercase tracking-wider text-slate-500">Comissões (vendas)</p>
            <p className="mt-2 font-display text-3xl text-emerald-600">{fmt(totalMargin)}</p>
            <p className="mt-1 text-xs text-slate-500">Por pagar {fmt(pendingMargin)} · Pago {fmt(paidMargin)}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="seller-tabs">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} data-testid={`seller-tab-${id}`} onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium border transition-colors ${
                tab === id ? "bg-[#0072C6] text-white border-[#0072C6]" : "bg-white text-slate-600 border-slate-200 hover:border-[#0072C6] hover:text-[#0072C6]"
              }`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {/* VIATURAS */}
        {tab === "viaturas" && (
          <div data-testid="seller-vehicles">
            <h2 className="font-display text-xl text-[#0072C6] mb-6">Stock de Viaturas</h2>
            {vehicles.length === 0 ? (
              <div className="bg-white border border-slate-200 p-10 text-center text-slate-400 text-sm">Ainda não há viaturas no stock.</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {vehicles.map((v) => {
                  const mine = myVehicleIds.has(v.id);
                  const taken = v.sold || (v.reserved && !mine);
                  return (
                    <div key={v.id} className="bg-white border border-slate-200 overflow-hidden flex flex-col" data-testid={`seller-vehicle-${v.id}`}>
                      {v.image_url || (v.images && v.images[0]) ? (
                        <img src={v.image_url || v.images[0]} alt={v.title} className="w-full h-44 object-cover" />
                      ) : (
                        <div className="w-full h-44 bg-slate-100 flex items-center justify-center text-slate-300"><Car className="w-10 h-10" /></div>
                      )}
                      <div className="p-5 flex-1 flex flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-semibold text-[#0072C6]">{v.brand} {v.model}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{v.title}</p>
                          </div>
                          {(v.sold || v.reserved) && (
                            <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 ${v.sold ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                              {v.sold ? "Vendida" : "Reservada"}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-2">{v.year} · {new Intl.NumberFormat("pt-PT").format(v.km || 0)} km · {v.fuel}</p>
                        <p className="mt-3 font-display text-lg text-[#0072C6]">{fmt(v.price)} <span className="text-xs text-slate-400 font-normal">(preço-base)</span></p>
                        <button onClick={() => openDealForm(v)} disabled={taken} data-testid={`seller-select-vehicle-${v.id}`}
                          className="mt-4 inline-flex items-center justify-center gap-2 bg-[#0072C6] hover:bg-[#005FA3] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2.5 transition-colors">
                          Selecionar para cliente
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* NEGOCIAÇÕES */}
        {tab === "negociacoes" && (
          <div data-testid="seller-deals">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl text-[#0072C6]">Clientes e Negociações</h2>
              <button onClick={() => setShowDealForm(!showDealForm)} data-testid="seller-new-deal"
                className="inline-flex items-center gap-2 bg-[#FF6A00] hover:bg-[#e65f00] text-white text-sm font-semibold px-5 py-2.5 transition-colors">
                {showDealForm ? "Fechar" : "Registar cliente"}
              </button>
            </div>

            {showDealForm && (
              <form onSubmit={submitDeal} className="bg-white border border-slate-200 p-6 sm:p-8 mb-8" data-testid="seller-deal-form">
                <h3 className="text-sm font-semibold text-[#0072C6] uppercase tracking-wider mb-5">Novo cliente / negociação</h3>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="d-client" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Nome do cliente *</label>
                    <input id="d-client" data-testid="deal-client-name" required minLength={2} value={dealForm.client_name} onChange={(e) => setDealForm((f) => ({ ...f, client_name: e.target.value }))}
                      className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                  </div>
                  <div>
                    <label htmlFor="d-phone" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Contacto telefónico *</label>
                    <input id="d-phone" data-testid="deal-client-phone" required minLength={6} value={dealForm.client_phone} onChange={(e) => setDealForm((f) => ({ ...f, client_phone: e.target.value }))}
                      className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                  </div>
                  <div>
                    <label htmlFor="d-email" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">E-mail *</label>
                    <input id="d-email" type="email" data-testid="deal-client-email" required value={dealForm.client_email} onChange={(e) => setDealForm((f) => ({ ...f, client_email: e.target.value }))}
                      className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                  </div>
                  <div>
                    <label htmlFor="d-vehicle" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Viatura pretendida *</label>
                    <select id="d-vehicle" data-testid="deal-vehicle" required value={dealForm.vehicle_id} onChange={(e) => setDealForm((f) => ({ ...f, vehicle_id: e.target.value }))}
                      className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] bg-white">
                      <option value="">Selecionar viatura…</option>
                      {vehicles.filter((v) => !v.sold).map((v) => (
                        <option key={v.id} value={v.id}>{v.brand} {v.model} — {fmt(v.price)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="d-margin" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">A sua margem (€) *</label>
                    <input id="d-margin" type="number" min="0" step="1" data-testid="deal-margin" required value={dealForm.margin} onChange={(e) => setDealForm((f) => ({ ...f, margin: e.target.value }))}
                      className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" placeholder="Ex.: 1000" />
                    {dealForm.vehicle_id && (
                      <p className="text-xs text-slate-500 mt-1.5">
                        Preço final: {fmt((vehicles.find((v) => v.id === dealForm.vehicle_id)?.price || 0) + (parseInt(dealForm.margin || "0", 10) || 0))}
                      </p>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="d-notes" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Observações</label>
                    <textarea id="d-notes" data-testid="deal-notes" rows={3} value={dealForm.notes} onChange={(e) => setDealForm((f) => ({ ...f, notes: e.target.value }))}
                      className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] resize-y" />
                  </div>
                </div>
                <button type="submit" disabled={dealSaving} data-testid="deal-submit"
                  className="mt-6 inline-flex items-center gap-2 bg-[#0072C6] hover:bg-[#005FA3] disabled:opacity-60 text-white text-sm font-semibold px-6 py-3 transition-colors">
                  {dealSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Registar cliente
                </button>
              </form>
            )}

            {clients.length === 0 ? (
              <div className="bg-white border border-slate-200 p-10 text-center text-slate-400 text-sm">Ainda não registou nenhum cliente.</div>
            ) : (
              <div className="space-y-4">
                {clients.map((d) => (
                  <div key={d.id} className="bg-white border border-slate-200 p-5" data-testid={`deal-card-${d.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-[#0072C6]">{d.client_name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{d.client_email} · {d.client_phone}</p>
                        <p className="text-sm text-slate-600 mt-2">{d.vehicle_brand} {d.vehicle_model} <span className="text-slate-400">({d.vehicle_title})</span></p>
                        <p className="text-xs text-slate-500 mt-1.5">
                          Base {fmt(d.base_price)} + Margem {fmt(d.margin)} = <span className="font-semibold text-emerald-600">{fmt(d.final_price)}</span>
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 ${DEAL_STATUS[d.status]?.cls || "bg-slate-100 text-slate-500"}`} data-testid={`deal-status-${d.id}`}>
                          {DEAL_STATUS[d.status]?.label || d.status}
                        </span>
                        <span className="text-xs text-slate-400">{new Date(d.created_at).toLocaleDateString("pt-PT")}</span>
                        <div className="flex items-center gap-1.5">
                          {d.status !== "reservada" && d.status !== "vendida" && (
                            <button onClick={() => setDealStatus(d, "reservada")} data-testid={`deal-reserve-${d.id}`} title="Marcar reservada"
                              className="p-1.5 text-slate-400 hover:text-amber-600 border border-slate-200 hover:border-amber-200 transition-colors">
                              <Bookmark className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {d.status !== "vendida" && (
                            <button onClick={() => setDealStatus(d, "vendida")} data-testid={`deal-sell-${d.id}`} title="Marcar vendida"
                              className="p-1.5 text-slate-400 hover:text-emerald-600 border border-slate-200 hover:border-emerald-200 transition-colors">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {d.status !== "vendida" && (
                            <button onClick={() => setDealStatus(d, "disponivel")} data-testid={`deal-release-${d.id}`} title="Libertar viatura"
                              className="p-1.5 text-slate-400 hover:text-slate-600 border border-slate-200 hover:border-slate-300 transition-colors">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button onClick={() => { setDocDeal(d); setTab("documentos"); }} data-testid={`deal-docs-${d.id}`} title="Documentos"
                            className="p-1.5 text-slate-400 hover:text-[#0072C6] border border-slate-200 hover:border-[#0072C6] transition-colors">
                            <FileText className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                    {d.notes && <p className="mt-3 text-sm text-slate-600 border-t border-slate-100 pt-3">{d.notes}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* DOCUMENTOS */}
        {tab === "documentos" && (
          <div data-testid="seller-documents">
            <h2 className="font-display text-xl text-[#0072C6] mb-6">Documentos Enviados</h2>
            {docDeal && (
              <form onSubmit={uploadDocs} className="bg-white border border-slate-200 p-6 mb-8" data-testid="seller-doc-form">
                <p className="text-sm font-semibold text-[#0072C6] mb-1">Enviar documentos — {docDeal.client_name}</p>
                <p className="text-xs text-slate-500 mb-4">{docDeal.vehicle_brand} {docDeal.vehicle_model}</p>
                <label className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2.5 text-sm text-slate-600 cursor-pointer hover:border-[#0072C6] hover:text-[#0072C6] transition-colors">
                  <Upload className="w-4 h-4" />
                  {docFiles.length > 0 ? `${docFiles.length} ficheiro(s)` : "Selecionar PDF, JPG ou PNG"}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple className="hidden" onChange={(e) => setDocFiles(Array.from(e.target.files || []))} data-testid="deal-doc-upload" />
                </label>
                <div className="mt-4 flex gap-3">
                  <button type="submit" disabled={docUploading || docFiles.length === 0} data-testid="deal-doc-submit"
                    className="inline-flex items-center gap-2 bg-[#0072C6] hover:bg-[#005FA3] disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 transition-colors">
                    {docUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Enviar
                  </button>
                  <button type="button" onClick={() => { setDocDeal(null); setDocFiles([]); }} className="border border-slate-300 text-slate-600 text-sm font-semibold px-5 py-2.5 hover:border-slate-400">Cancelar</button>
                </div>
              </form>
            )}
            {deals.every((d) => (d.documents || []).length === 0) ? (
              <div className="bg-white border border-slate-200 p-10 text-center text-slate-400 text-sm">Ainda não enviou documentos. Selecione uma negociação para anexar documentos do cliente.</div>
            ) : (
              <div className="space-y-4">
                {deals.filter((d) => (d.documents || []).length > 0).map((d) => (
                  <div key={d.id} className="bg-white border border-slate-200 p-5">
                    <p className="font-semibold text-sm text-[#0072C6]">{d.client_name} — {d.vehicle_brand} {d.vehicle_model}</p>
                    <p className="text-xs text-slate-500 mt-1">{(d.documents || []).length} documento(s) enviado(s)</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PERFIL */}
        {tab === "perfil" && me && (
          <div className="max-w-2xl" data-testid="seller-profile">
            <div className="bg-white border border-slate-200 p-6 sm:p-8 mb-6">
              <h2 className="font-display text-xl text-[#0072C6] mb-6">O Meu Perfil</h2>
              <div className="flex items-center gap-5 mb-6">
                {me.photo_url ? (
                  <img src={me.photo_url} alt={me.name} className="w-20 h-20 object-cover border border-slate-200" />
                ) : (
                  <div className="w-20 h-20 bg-slate-100 flex items-center justify-center text-slate-300"><User className="w-8 h-8" /></div>
                )}
                <div>
                  <p className="font-semibold text-[#0072C6]">{me.name}</p>
                  <p className="text-sm text-slate-500">{me.email}</p>
                  <p className="text-sm text-slate-500">{me.phone}</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Nome</label>
                  <input value={me.name || ""} onChange={(e) => setMe((m) => ({ ...m, name: e.target.value }))}
                    className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Telemóvel</label>
                  <input value={me.phone || ""} onChange={(e) => setMe((m) => ({ ...m, phone: e.target.value }))}
                    className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
              </div>
            </div>
            <form onSubmit={savePassword} className="bg-white border border-slate-200 p-6 sm:p-8" data-testid="seller-password-form">
              <h3 className="text-sm font-semibold text-[#0072C6] uppercase tracking-wider mb-5">Alterar palavra-passe</h3>
              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Palavra-passe atual</label>
                  <input type="password" data-testid="seller-pw-current" required value={pw.current_password} onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))}
                    className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Nova palavra-passe</label>
                    <input type="password" data-testid="seller-pw-new" required minLength={8} value={pw.new_password} onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))}
                      className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Confirmar</label>
                    <input type="password" data-testid="seller-pw-confirm" required minLength={8} value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
                      className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
                  </div>
                </div>
                <button type="submit" disabled={pwSaving} data-testid="seller-pw-submit"
                  className="inline-flex items-center gap-2 bg-[#0072C6] hover:bg-[#005FA3] disabled:opacity-60 text-white text-sm font-semibold px-6 py-3 transition-colors">
                  {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Guardar palavra-passe
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
